import {isEqual} from "es-toolkit";
import type {Query} from "../../cdx-models/utils/MakeModel";
import {Changes} from "./Changes";
import type createCollector from "./collector";
import type {
  AddToCacheData,
  CacheEntry as Entry,
  CacheVal,
  ChangeListener,
  CollectType,
  CreatedIdsFn,
  FieldDependents,
  ModelCache,
  ModelDesc,
  ModelDescriptions,
  ModelName,
  MutationDesc,
  Tx,
} from "./mate-types";
import {getIdAsKey, hydrateField, INVALIDATE} from "./mate-shared";
import {
  invalidateAfterDelete,
  invalidateAfterUpdate,
  invalidateListsAfterCreateOrDelete,
  type InvalidationContext,
} from "./invalidation-helpers";
import {
  ABSENT,
  CachedLists,
  type FieldsAffectingRelations,
  type KeysByParent,
  type ParentRef,
} from "./list-targets";
import {
  diffOverlays,
  isPlaceholderId,
  mutationIdOfPlaceholder,
  OverlayBuilder,
  placeholderId,
  type CreatedRow,
  type LayerPart,
  type OverlayReads,
  type OverlayRows,
} from "./optimistic";
import {processQuery} from "./process-query";

const hydrateValue = (dbEntry: Record<string, any>, desc: ModelDesc) =>
  Object.fromEntries(
    Object.entries(dbEntry).map(([k, v]) => [k, hydrateField(desc.fields[k]?.type, v)])
  );

const updateExistingValuesAndDeleteStatus = <T extends Record<string, any>>(
  entry: Entry<T>,
  newData: Partial<T>
) => {
  let hasChanged = false;
  const existingData = entry.value;

  for (const field in newData) {
    const newValue = newData[field]!;
    if (!(field in existingData) || !isEqual(existingData[field], newValue)) {
      existingData[field] = newValue;
      hasChanged = true;
    }
    delete entry.status[field];
  }
  return hasChanged;
};

/** Throws on a name in `fieldDependents` that isn't a field or foreign key of its model. */
function checkFieldDependents(descriptions: ModelDescriptions, fieldDependents: FieldDependents) {
  for (const [modelName, byField] of Object.entries(fieldDependents) as [
    ModelName,
    Record<string, string[]>,
  ][]) {
    const desc = descriptions[modelName];
    if (!desc) throw new Error(`fieldDependents: unknown model '${modelName}'`);
    for (const [field, dependents] of Object.entries(byField)) {
      for (const name of [field, ...dependents]) {
        if (!(name in desc.fields) && !(name in desc.fkToBelongsTo)) {
          throw new Error(`fieldDependents: '${modelName}' has no field or foreign key '${name}'`);
        }
      }
    }
  }
}

/**
 * For each model, the relations of other models that lead to its rows:
 * `hasMany` gives {attachment: [{model: "card", relName: "attachments", fk: "cardId"}]},
 * `belongsTo` gives {milestone: [{model: "card", relName: "milestone", fk: "milestoneId"}]}.
 */
function collectIncomingRelations(descriptions: ModelDescriptions, kind: "hasMany" | "belongsTo") {
  const modelNameEntries = Object.entries(descriptions) as [ModelName, ModelDesc][];
  const deps = Object.fromEntries(
    modelNameEntries.map(([modelName]) => [modelName, [] as ParentRef[]])
  ) as Record<ModelName, ParentRef[]>;
  for (const [modelName, desc] of modelNameEntries) {
    const rels: Record<string, {model: ModelName; fk: string | null}> = desc[kind];
    for (const [relName, {model, fk}] of Object.entries(rels)) {
      // a retired relation; instance-pool answers it with nothing
      if ((model as string) === "$deprecated") continue;
      deps[model].push({model: modelName, relName, fk});
    }
  }
  return deps;
}

export const cardAccountSeqToCardId = new Map();

type OnChange = (changes: Changes["changes"] | "all") => void;

/**
 * A resolved mutation keeps showing its result until the fields its resolve marked invalid are
 * loaded again. A field still "invalid" after this long is read by nothing on screen.
 */
const UNREAD_GRACE_MS = 100;
/** A resolved mutation stops showing its result after this long, reloaded or not. */
const MAX_RESOLVED_MS = 15_000;

type Layer = {
  mutationId: number;
  parts: LayerPart[];
  createdIdsFns: CreatedIdsFn[];
  /** the placeholder rows the last run created */
  created: CreatedRow[];
  resolvedAt: number | null;
  /** fields the resolve marked invalid that haven't been reloaded yet */
  awaiting: [Entry, string][];
};

export type PlaceholderState = {state: "mapped"; id: string} | {state: "pending"} | {state: "gone"};

export default function createCache(
  descriptions: ModelDescriptions,
  fieldDependents: FieldDependents,
  collector: ReturnType<typeof createCollector>,
  onChange: OnChange,
  registerOnChangeListener: ChangeListener,
  onClear?: () => void
) {
  checkFieldDependents(descriptions, fieldDependents);
  const hasManyModelDeps = collectIncomingRelations(descriptions, "hasMany");
  const belongsToModelDeps = collectIncomingRelations(descriptions, "belongsTo");

  const onEntryLoadedHooks: Partial<Record<ModelName, (entry: any) => void>> = {
    card: (entry) => {
      if (entry.accountSeq) {
        cardAccountSeqToCardId.set(entry.accountSeq, entry.cardId);
      }
    },
  };

  const modelCache: ModelCache = {};
  // {card: {title: {account: Set(["cards({title: 'something'}"])}}
  // queries on the relation's own rows: which lists a row write changes is told by `affectedLists`
  const fieldsAffectingRelations: FieldsAffectingRelations = {};
  // queries through a relation, e.g. `account.cardOrders({card: {deckId}})` under card.deckId: any
  // parent's list may hold the rows they reach
  const fieldsAffectingOtherRelations: FieldsAffectingRelations = {};

  const relsAffectingRelations: Partial<Record<ModelName, KeysByParent>> = {};
  /*
    sample rel:
    account.$meta.find("cards", {handCards: {userId: 3}})

    result in relsAffectingRelations
    {handCards: {account: Set(["cards(...)"])}}

    note that handCard's cardId will be mapped in `fieldsAffectingRelations`

  */

  let layers: Layer[] = [];
  let overlayRows: OverlayRows = new Map();
  let overlayReads: OverlayReads = new Map();
  const inFlightMutationIds = new Set<number>();
  // lives for the session: UI state may hold a placeholder long after its row reloaded
  const placeholderToId = new Map<string, string>();

  /** Set while a resolve invalidates, to learn which fields its layer waits for. */
  let invalidationRecorder: Map<Entry, Set<string>> | null = null;
  const markInvalid = (entry: Entry, field: string) => {
    entry.status[field] = "invalid";
    if (!invalidationRecorder) return;
    const fields = invalidationRecorder.get(entry);
    if (fields) fields.add(field);
    else invalidationRecorder.set(entry, new Set([field]));
  };

  const getModelFromCache = (modelName: ModelName, id: string): Record<string, any> | undefined => {
    const mCache = modelCache[modelName];
    if (!mCache) return undefined;
    const eCache = mCache[id];
    return eCache && eCache.value;
  };

  // --- optimistic layers ---

  /** `update`/`delete` rows by the ids in `data`, the way the invalidation reads them. */
  const splitIds = (model: ModelName, data: Record<string, any>) => {
    const {idPropAsArray} = descriptions[model];
    const idParts = idPropAsArray.map((prop) => data[prop]);
    const {id, ids, ...rest} = data;
    if (idParts.every(Boolean)) {
      for (const prop of idPropAsArray) delete rest[prop];
      return {ids: [getIdAsKey(idParts)], fields: rest};
    }
    return {ids: (id ? [id] : ids ?? []) as string[], fields: rest};
  };

  const generatedPart = (
    desc: MutationDesc & {type: "update" | "create" | "delete"},
    data: Record<string, any>
  ): LayerPart | null => {
    const {model} = desc;
    switch (desc.type) {
      case "update": {
        const {ids, fields} = splitIds(model, data);
        if (!ids.length || !Object.keys(fields).length) return null;
        return (tx) => ids.forEach((id) => tx.update(model, id, fields));
      }
      case "delete": {
        const {ids} = splitIds(model, data);
        if (!ids.length) return null;
        return (tx) => ids.forEach((id) => tx.delete(model, id));
      }
      case "create": {
        const modelDesc = descriptions[model];
        const fields = Object.fromEntries(
          Object.entries(data).filter(
            ([field]) =>
              modelDesc.idPropAsArray.includes(field) ||
              (field !== "id" &&
                (modelDesc.fkToBelongsTo[field] ||
                  modelDesc.fields[field] ||
                  modelDesc.belongsTo[field]))
          )
        );
        if (Object.keys(fields).every((field) => modelDesc.idPropAsArray.includes(field))) {
          return null;
        }
        return (tx) => {
          tx.create(model, fields);
        };
      }
      default:
        return null;
    }
  };

  const collectParts = (
    desc: MutationDesc,
    rawData: Record<string, any>,
    isImplicit: boolean,
    layer: Pick<Layer, "parts" | "createdIdsFns">
  ) => {
    const data = desc.convertDataForOptimistic ? desc.convertDataForOptimistic(rawData) : rawData;
    if (desc.optimistic) {
      const {optimistic} = desc;
      layer.parts.push((tx: Tx) => optimistic(tx, data));
    } else if (desc.type !== "void" && !(desc.type === "create" && isImplicit)) {
      // An implicit create only says "some row got made"; it has too little to show one.
      const part = generatedPart(desc, data);
      if (part) layer.parts.push(part);
    }
    if (desc.createdIds) layer.createdIdsFns.push(desc.createdIds);
    if (desc.implicit) {
      const list = desc.implicit(data, {
        _isOptimistic: true,
        _getModelFromCache: getModelFromCache,
      });
      for (const {desc: implDesc, data: implData} of list) {
        collectParts(implDesc, implData, true, layer);
      }
    }
  };

  const invalidationContext: InvalidationContext = {
    descriptions,
    hasManyModelDeps,
    fieldsAffectingRelations,
    modelCache,
    fieldsAffectingOtherRelations,
    relsAffectingRelations,
    fieldDependents,
    belongsToModelDeps,
    markInvalid,
  };

  /**
   * The loaded cache's lists. Kept across invalidations, e.g. a burst of websocket messages, until
   * `add` or `clear` changes what's loaded; invalidating only marks fields and writes row fields.
   */
  let sharedLists: CachedLists | null = null;
  const loadedLists = () =>
    (sharedLists ??= new CachedLists(modelCache, (model, id, key) => {
      const entry =
        model === "_root" ? (modelCache._root as unknown as Entry) : modelCache[model]?.[id];
      return entry && key in entry.value ? entry.value[key] : ABSENT;
    }));

  const overlayContext = {
    descriptions,
    hasManyModelDeps,
    fieldsAffectingRelations,
    modelCache,
    getRelationKey: (
      model: ModelName,
      relName: string,
      query: Record<string, unknown> | null | undefined
    ) =>
      query && Object.keys(query).length
        ? processQuery({
            modelDesc: descriptions[model],
            query: query as Query<any>,
            relName,
            descriptions,
            cache,
          }).fullRelName
        : relName,
    getSavedId: (placeholder: string) => placeholderToId.get(placeholder),
  };

  /** Runs every pending mutation again on the loaded cache and adds what differs to `changes`. */
  const rebuild = (changes: Changes) => {
    const prev = overlayRows;
    if (layers.length) {
      const builder = new OverlayBuilder(overlayContext);
      for (const layer of layers) {
        layer.created = builder.runLayer(layer.parts, layer.mutationId, layer.resolvedAt !== null);
      }
      overlayRows = builder.finish();
      overlayReads = builder.reads;
    } else {
      overlayRows = new Map();
      overlayReads = new Map();
    }
    diffOverlays(prev, overlayRows, (model, id) =>
      changes.add(model, model === "_root" ? null : id)
    );
    return changes;
  };

  /** Whether `changes` hold a loaded row the last build read, so building again can differ. */
  const touchesOverlayReads = ({changes: {updated, touched}}: Changes) => {
    for (const byModel of [updated, touched]) {
      for (const [model, ids] of byModel) {
        const read = overlayReads.get(model);
        if (!read) continue;
        if (read === true) return true;
        for (const id of ids) if (read.has((id as string | null) ?? "_root")) return true;
      }
    }
    return false;
  };

  /** Drops resolved layers whose data has been reloaded; returns whether any was dropped. */
  const dropSettledLayers = () => {
    const now = Date.now();
    const before = layers.length;
    layers = layers.filter((layer) => {
      if (layer.resolvedAt === null) return true;
      const age = now - layer.resolvedAt;
      if (age >= MAX_RESOLVED_MS) return false;
      layer.awaiting = layer.awaiting.filter(([entry, field]) => {
        const status = entry.status[field];
        if (status === "loading" || status === "refreshing") return true;
        return status === "invalid" && age < UNREAD_GRACE_MS;
      });
      // a reloaded list can name a saved row before that row's own fields are loaded
      const isRowPending = ({model, id, savedFields}: CreatedRow) => {
        const entry = modelCache[model]?.[id];
        return savedFields!.some((field) => {
          const fk = descriptions[model].belongsTo[field]?.fk;
          if (entry && (field in entry.value || (fk && fk in entry.value))) return false;
          const status = entry?.status[field];
          return status === "loading" || status === "refreshing" || age < UNREAD_GRACE_MS;
        });
      };
      return (
        layer.awaiting.length > 0 ||
        layer.created.some((row) => row.savedFields && isRowPending(row))
      );
    });
    return layers.length !== before;
  };

  const checkLayersLater = (ms: number) =>
    setTimeout(() => {
      if (dropSettledLayers()) onChange(rebuild(new Changes()).changes);
    }, ms);

  const cache = {
    modelCache,
    add(data: AddToCacheData) {
      sharedLists = null;
      const changes = new Changes();
      for (const [modelName, record] of Object.entries(data) as [ModelName, unknown][]) {
        if (!(modelName in descriptions)) throw new Error(`Don't know model "${modelName}"`);
        if (modelName === "_root") {
          const existing = modelCache[modelName] as any as Entry;
          const entry = record as Record<string, any>;
          if (existing) {
            const hasUpdated = updateExistingValuesAndDeleteStatus(existing, entry);
            if (hasUpdated) {
              changes.add(modelName, null);
            } else {
              changes.add(modelName, null, "touched");
            }
          } else {
            (modelCache[modelName] as any) = {
              value: entry,
              status: {},
            };
          }
        } else {
          const modelEntries = (modelCache[modelName] = modelCache[modelName] || {});
          const hook = onEntryLoadedHooks[modelName];
          const desc = descriptions[modelName];
          for (const [entryId, dbEntry] of Object.entries(record as any)) {
            if (dbEntry === null) {
              if (process.env.NODE_ENV !== "production") {
                console.warn(
                  `trying to add ${modelName}(${entryId}) to cache, but it's deleted/inaccessible`
                );
              }
              changes.add(modelName, entryId);
              modelEntries[entryId] = null;
            } else {
              const hydrated = hydrateValue(dbEntry as any, desc);
              if (hook) hook(hydrated);
              const existing = modelEntries[entryId];
              if (existing) {
                const hasUpdated = updateExistingValuesAndDeleteStatus(existing, hydrated);
                if (hasUpdated) {
                  changes.add(modelName, entryId);
                } else {
                  changes.add(modelName, entryId, "touched");
                }
              } else {
                changes.add(modelName, entryId);
                modelEntries[entryId] = {
                  value: hydrated,
                  status: {},
                };
              }
            }
          }
        }
      }
      // rows no pending mutation read can't change what the mutations show
      if (layers.length && (dropSettledLayers() || touchesOverlayReads(changes))) rebuild(changes);
      onChange(changes.changes);
    },
    // hint: could also be just inaccessible.
    isDeleted(modelName: ModelName, id: string) {
      const modelEntries = modelCache[modelName];
      if (!modelEntries) return false;
      return modelEntries[id] === null;
    },
    // status: "loaded" | "loading" | "refreshing" | "optimistic" | "notLoaded" | "invalid" | "deleted"
    getAndTellIfLoaded(opts: {
      modelName: ModelName;
      id: string;
      field: string;
      collectIfMissing?: false | CollectType;
    }): CacheVal {
      const {modelName, id, field, collectIfMissing} = opts;
      const cachedVals = modelCache[modelName];
      let cachedEntry: Entry | null | undefined;
      if (modelName === "_root") {
        cachedEntry = cachedVals as any as Entry;
      } else {
        cachedEntry = cachedVals && cachedVals[id];
      }
      const optRow = overlayRows.get(modelName)?.get(modelName === "_root" ? "_root" : id);
      if (optRow) {
        if (field in optRow.fields) {
          // A resolved mutation shows its result until the loaded cache has it, so ask for it here:
          // an invalidated field again, and the fields of a row the server has saved.
          if (
            collectIfMissing &&
            modelName !== "_root" &&
            !optRow.created &&
            !isPlaceholderId(id)
          ) {
            if (cachedEntry?.status[field] === "invalid") {
              collector.collect([{modelName, id}], field, collectIfMissing, "invalid");
              cachedEntry.status[field] = "refreshing";
            } else if (
              !cachedEntry ||
              (!(field in cachedEntry.value) && !cachedEntry.status[field])
            ) {
              const entry = cachedEntry ?? {value: {}, status: {}};
              if (!cachedEntry) {
                if (!modelCache[modelName]) modelCache[modelName] = {};
                modelCache[modelName][id] = entry;
              }
              collector.collect([{modelName, id}], field, collectIfMissing);
              entry.status[field] = "loading";
            }
          }
          const val = optRow.fields[field];
          if (val === INVALIDATE) {
            // api.ts told us: we don't know the new value. Fall back to real cache or default.
            if (cachedEntry && field in cachedEntry.value) {
              return {
                val: cachedEntry.value[field],
                isLoaded: false,
                status: "optimistic",
                useDefaultVal: false,
              };
            }
            return {val: undefined, isLoaded: false, status: "optimistic", useDefaultVal: true};
          }
          return {val, isLoaded: false, status: "optimistic", useDefaultVal: false};
        }
        if (optRow.created) {
          return {val: undefined, isLoaded: false, status: "optimistic", useDefaultVal: true};
        }
      }
      if (isPlaceholderId(id)) {
        // the server doesn't know this id
        return {val: undefined, isLoaded: false, status: "notLoaded", useDefaultVal: true};
      }

      if (cachedEntry === null) {
        console.warn(
          `tried to get "${field}" on "${modelName}" with id "${id}" which is a deleted/inaccessible resource. Prevent this via checking for \`$meta.isDeleted()\``
        );
        return {val: null, isLoaded: true, status: "deleted", useDefaultVal: false};
      }
      if (cachedEntry === undefined) {
        if (collectIfMissing) {
          cachedEntry = {
            value: {},
            status: {},
          };
          if (modelName === "_root") {
            (modelCache as any)[modelName] = cachedEntry;
          } else {
            if (!modelCache[modelName]) modelCache[modelName] = {};
            modelCache[modelName][id] = cachedEntry;
          }
          collector.collect([{modelName, id}], field, collectIfMissing);
          cachedEntry.status[field] = "loading";
        }
        return {
          val: undefined,
          isLoaded: false,
          status: collectIfMissing ? "loading" : "notLoaded",
          useDefaultVal: true,
        };
      } else {
        const isFieldPresent = field in cachedEntry.value;
        const status = cachedEntry.status[field];
        const needsToCollect = !isFieldPresent || Boolean(status);

        if (collectIfMissing && needsToCollect && status !== "loading" && status !== "refreshing") {
          const desc = descriptions[modelName];
          if (desc && desc.isFunction) {
            // there's no way to update rows returned from a function. So let's just ignore those
            delete cachedEntry.status[field];
          } else {
            collector.collect([{modelName, id}], field, collectIfMissing, status);
          }
          cachedEntry.status[field] = status === "invalid" ? "refreshing" : "loading";
        }

        return isFieldPresent
          ? {
              val: cachedEntry.value[field],
              isLoaded: !needsToCollect,
              status: cachedEntry.status[field] || "loaded",
              useDefaultVal: false,
            }
          : {
              val: undefined,
              isLoaded: !needsToCollect,
              status: cachedEntry.status[field] || "notLoaded",
              useDefaultVal: true,
            };
      }
    },
    /** Whether a pending mutation created this row; nothing is loaded for it until it's saved. */
    isOptimisticRow(modelName: ModelName, id: string) {
      return Boolean(overlayRows.get(modelName)?.get(id)?.created);
    },
    getPlaceholderState(id: string): PlaceholderState {
      const mapped = placeholderToId.get(id);
      if (mapped) return {state: "mapped", id: mapped};
      return inFlightMutationIds.has(mutationIdOfPlaceholder(id))
        ? {state: "pending"}
        : {state: "gone"};
    },
    clear({onDone, delayReload}: {onDone?: () => void; delayReload?: boolean} = {}) {
      sharedLists = null;
      collector.clear();
      onClear?.();
      if (delayReload) {
        collector.delayCollecting(50, () => onChange("all"));
      }
      for (const key of Object.keys(modelCache) as ModelName[]) {
        delete modelCache[key];
      }
      layers = [];
      overlayRows = new Map();
      overlayReads = new Map();
      if (onDone) {
        const unsub = registerOnChangeListener(() => {
          onDone();
          unsub();
        });
      }
      onChange("all");
    },
    invalidateAll() {
      const invalidateAllFields = ({value, status}: Entry) => {
        for (const field in value) status[field] = "invalid";
      };
      for (const [key, val] of Object.entries(modelCache)) {
        if (key === "_root") {
          invalidateAllFields(val as any as Entry);
        } else {
          for (const model of Object.values(val)) {
            if (model) invalidateAllFields(model);
          }
        }
      }
      onChange("all");
    },
    /** Shows a mutation's result right away, on top of the mutations started before it. */
    addLayer(mutationId: number, desc: MutationDesc, data: Record<string, any>) {
      const layer: Layer = {
        mutationId,
        parts: [],
        createdIdsFns: [],
        created: [],
        resolvedAt: null,
        awaiting: [],
      };
      collectParts(desc, data, false, layer);
      inFlightMutationIds.add(mutationId);
      layers.push(layer);
      onChange(rebuild(new Changes()).changes);
    },
    /** For a request that was held until the rows it refers to were saved. */
    replaceLayerData(mutationId: number, desc: MutationDesc, data: Record<string, any>) {
      const layer = layers.find((l) => l.mutationId === mutationId);
      if (!layer) return;
      layer.parts = [];
      layer.createdIdsFns = [];
      collectParts(desc, data, false, layer);
      onChange(rebuild(new Changes()).changes);
    },
    failLayer(mutationId: number) {
      inFlightMutationIds.delete(mutationId);
      layers = layers.filter((l) => l.mutationId !== mutationId);
      onChange(rebuild(new Changes()).changes);
    },
    /**
     * Invalidates what the mutation changed. Its layer keeps showing the result until those fields
     * are loaded again, so the old data never shows in between.
     */
    resolveLayer(
      mutationId: number,
      desc: MutationDesc,
      data: Record<string, any>,
      retVal: any,
      path: string
    ) {
      inFlightMutationIds.delete(mutationId);
      const layer = layers.find((l) => l.mutationId === mutationId);
      const recorded = new Map<Entry, Set<string>>();
      invalidationRecorder = recorded;
      const changes = new Changes();
      try {
        cache.invalidate(desc, data, retVal, true, changes);
      } finally {
        invalidationRecorder = null;
      }
      if (layer) {
        const mapIds = (model: ModelName, ids: string | Record<string, string> | undefined) => {
          if (typeof ids === "string") placeholderToId.set(placeholderId(mutationId, model), ids);
          else if (ids) {
            for (const [key, id] of Object.entries(ids)) {
              placeholderToId.set(placeholderId(mutationId, model, key), id);
            }
          }
        };
        for (const fn of layer.createdIdsFns) {
          for (const [model, ids] of Object.entries(fn(retVal) ?? {}) as [ModelName, any][]) {
            mapIds(model, ids);
          }
        }
        const [onlyCreated, ...moreCreated] = layer.created.filter((row) => !row.savedFields);
        if (
          !layer.createdIdsFns.length &&
          onlyCreated &&
          !moreCreated.length &&
          onlyCreated.key === undefined &&
          typeof retVal?.id === "string"
        ) {
          placeholderToId.set(onlyCreated.id, retVal.id);
        }
        if (process.env.NODE_ENV !== "production") {
          const unmapped = layer.created.filter(
            (row) => !row.savedFields && !placeholderToId.has(row.id)
          );
          if (unmapped.length) {
            console.error(
              `"${path}" resolved without ids for ${unmapped.map((r) => r.id).join(", ")}; ` +
                "return `{id}` from the server or add `createdIds`, or the row may show twice"
            );
          }
        }
        layer.resolvedAt = Date.now();
        layer.awaiting = [...recorded].flatMap(([entry, fields]) =>
          [...fields].map((field): [Entry, string] => [entry, field])
        );
        dropSettledLayers();
        if (layers.includes(layer)) {
          checkLayersLater(UNREAD_GRACE_MS);
          checkLayersLater(MAX_RESOLVED_MS);
        }
      }
      if (layers.length || overlayRows.size) rebuild(changes);
      onChange(changes.changes);
    },
    invalidate(
      desc: MutationDesc,
      data: Record<string, any>,
      retVal: any,
      isImplicit = false,
      changes = new Changes(),
      lists = loadedLists()
    ) {
      switch (desc.type) {
        case "update":
          invalidateAfterUpdate({ctx: invalidationContext, desc, data, changes, lists});
          break;
        case "create":
        case "delete": {
          const bag = {ctx: invalidationContext, desc, data, changes, lists};
          // lists first: they read which of the row's fields were stale before the delete
          invalidateListsAfterCreateOrDelete(bag);
          if (desc.type === "delete") invalidateAfterDelete(bag);
          break;
        }
        case "void":
          // do nothing. This type exists just for implicit updates
          break;
        default:
          throw new Error(`don't know action type "${(desc as {type: string}).type}"!`);
      }
      if (desc.implicit) {
        const invalidations = desc.implicit(data, {
          ...retVal,
          _getModelFromCache: getModelFromCache,
        });
        for (const {desc: implDesc, data: implData} of invalidations) {
          cache.invalidate(implDesc, implData, retVal, true, changes, lists);
        }
      }
      if (!isImplicit) {
        if (layers.length) rebuild(changes);
        onChange(changes.changes);
      }
    },

    addConstraintBasedRelationField({
      parentModel,
      relName,
      targetModel,
      field,
      isOwnRow,
    }: {
      parentModel: ModelName;
      relName: string;
      targetModel: ModelName;
      field: string;
      /** the relation holds rows of `targetModel` and the query reads `field` on them directly */
      isOwnRow: boolean;
    }) {
      const map = isOwnRow ? fieldsAffectingRelations : fieldsAffectingOtherRelations;
      let byModel = map[targetModel];
      if (!byModel) byModel = map[targetModel] = {};
      let byField = byModel[field];
      if (!byField) byField = byModel[field] = {};
      let parentSet = byField[parentModel];
      if (!parentSet) parentSet = byField[parentModel] = new Set();
      parentSet.add(relName);
    },

    addConstraintBasedRelationRelation({
      parentModel,
      relName,
      targetModel,
      fk,
    }: {
      parentModel: ModelName;
      relName: string;
      targetModel: ModelName;
      fk: string;
    }) {
      /*
      sample rel:
      account.$meta.find("cards", {handCards: {userId: 3}})

      targetModelDesc: "card"
      parentModel: "account" + relName
      targetModel: "handCard"
      fk: cardId
      */

      // if a handCard's cardId is changed, invalidate the relation
      cache.addConstraintBasedRelationField({
        parentModel,
        relName,
        targetModel,
        field: fk,
        isOwnRow: false,
      });

      let byModel = relsAffectingRelations[targetModel];
      if (!byModel) byModel = relsAffectingRelations[targetModel] = {};
      let parentSet = byModel[parentModel];
      if (!parentSet) parentSet = byModel[parentModel] = new Set();
      parentSet.add(relName);
    },
  };
  return cache;
}
